import { CheckCircle2, ChevronRight, Info, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/occurrences/button'
import {
	JustificationDialog,
	Notice,
	Panel,
} from '@/components/occurrences/occurrence-ui'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
	MATRIX_QUESTIONS,
	MATRIX_VERSION,
	walkMatrix,
} from '@/mocks/occurrences/matrix'
import { can } from '@/mocks/occurrences/rules'
import { useOccurrenceMock } from '@/mocks/occurrences/store'
import type { MatrixAnswer, MatrixResult } from '@/types/occurrence'
import { MATRIX_RESULT_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

const ANSWER_LABEL = {
	YES: 'Sim',
	NO: 'Não',
	NA: 'Informação insuficiente',
} as const

export const MatrixSection = () => {
	const { occ, profile, editable, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const m = occ.matrix
	const walk = walkMatrix(m.answers)
	const [justification, setJustification] = useState('')
	const [editing, setEditing] = useState<string | null>(null)
	const [reopen, setReopen] = useState(false)
	const [note, setNote] = useState(m.committeeNote)

	const validated = Boolean(m.validatedBy)
	const canFill =
		editable &&
		!validated &&
		(profile === 'TECHNICIAN' || profile === 'SSMA_MANAGER') &&
		occ.status !== 'AWAITING_VALIDATION'
	const isManager = can(profile, 'concludeInvestigation')

	const progress = Math.round(
		(walk.path.length /
			(walk.path.length +
				(walk.done ? 0 : Math.max(1, 11 - walk.path.length)))) *
			100,
	)

	/** Salva uma resposta e recalcula: respostas que saíram do caminho são descartadas e a conclusão é invalidada. */
	const answer = (questionId: string, a: 'YES' | 'NO' | 'NA') => {
		const next: MatrixAnswer[] = [
			...m.answers.filter((x) => x.questionId !== questionId),
			{ questionId, answer: a, justification: justification.trim() },
		]
		const w = walkMatrix(next)
		const keep = new Set([
			...w.path.map((q) => q.id),
			...(w.current ? [w.current.id] : []),
		])
		const pruned = next.filter((x) => keep.has(x.questionId))
		update(
			occ.id,
			{
				matrix: {
					...m,
					answers: pruned,
					result: null,
					factors: [],
					validatedBy: null,
					validatedAt: null,
				},
			},
			`Matriz: ${questionId} respondida (${ANSWER_LABEL[a]})`,
			{
				detail: justification.trim(),
				diff: m.answers.find((x) => x.questionId === questionId)
					? {
							field: questionId,
							from: ANSWER_LABEL[
								m.answers.find((x) => x.questionId === questionId)?.answer ??
									'NA'
							],
							to: ANSWER_LABEL[a],
						}
					: undefined,
			},
		)
		setJustification('')
		setEditing(null)
	}

	const conclude = (result: MatrixResult) =>
		update(
			occ.id,
			{ matrix: { ...m, result, factors: walk.factors } },
			'Matriz concluída',
			{ detail: MATRIX_RESULT_LABEL[result] },
		)

	const validate = () =>
		update(
			occ.id,
			{
				matrix: {
					...m,
					validatedBy: userName,
					validatedAt: new Date().toISOString().slice(0, 16),
					committeeNote: note,
				},
			},
			'Matriz validada pelo Gestor SSMA e comitê',
			{ detail: note },
		)

	const prelim = walk.prelim
	const ambiguous = Array.isArray(prelim)
	const questionToShow = editing
		? MATRIX_QUESTIONS.find((q) => q.id === editing)
		: walk.current

	return (
		<div className="space-y-4">
			<Notice tone="warn">
				<b>Finalidade exclusivamente investigativa e preventiva.</b> O resultado
				não recomenda, aplica nem vincula medida disciplinar automaticamente.
				Versão da matriz: {m.version || MATRIX_VERSION}.
			</Notice>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				<div className="space-y-4 lg:col-span-2">
					{questionToShow && canFill ? (
						<Panel
							title={`${questionToShow.id} — pergunta ${walk.path.length + (editing ? 0 : 1)}`}
							description={
								editing
									? 'Alterar esta resposta recalcula as perguntas seguintes e invalida a conclusão.'
									: undefined
							}
						>
							<p className="text-base font-medium text-kaefer-dark-grey">
								{questionToShow.text}
							</p>
							<p className="flex items-start gap-1.5 text-xs text-muted-foreground">
								<Info className="mt-0.5 size-3.5 shrink-0" />
								{questionToShow.help}
							</p>
							<p className="rounded bg-muted/40 p-2 text-xs">
								<b>Evidência mínima:</b> {questionToShow.evidence}
							</p>
							<Textarea
								rows={3}
								value={justification}
								onChange={(e) => setJustification(e.target.value)}
								placeholder="Justificativa (obrigatória) — cite evidências objetivas"
							/>
							<div className="flex flex-wrap gap-2">
								<Button
									variant="primary"
									disabled={justification.trim().length < 5}
									onClick={() => answer(questionToShow.id, 'YES')}
								>
									Sim
								</Button>
								<Button
									variant="primary"
									disabled={justification.trim().length < 5}
									onClick={() => answer(questionToShow.id, 'NO')}
								>
									Não
								</Button>
								<Button
									variant="outline"
									disabled={justification.trim().length < 5}
									onClick={() => answer(questionToShow.id, 'NA')}
								>
									Informação insuficiente
								</Button>
								{editing && (
									<Button
										variant="cancel"
										onClick={() => {
											setEditing(null)
											setJustification('')
										}}
									>
										Cancelar edição
									</Button>
								)}
							</div>
							<p className="text-[11px] text-muted-foreground">
								“Informação insuficiente” encerra o fluxo como Análise
								Inconclusiva e gera pendências para obter a evidência.
							</p>
						</Panel>
					) : walk.done ? (
						<Panel title="Resultado da análise">
							{!canFill && !validated && (
								<Notice>
									Preenchimento concluído
									{validated ? '' : ' — aguardando validação do Gestor SSMA'}.
								</Notice>
							)}
							{ambiguous ? (
								<div className="space-y-2">
									<p className="text-sm">
										O caminho levou a um resultado preliminar com duas
										possibilidades. Escolha conforme os fatores:
									</p>
									<div className="flex gap-2">
										{(prelim as MatrixResult[]).map((r) => (
											<Button
												key={r}
												disabled={!canFill}
												variant={m.result === r ? 'primary' : 'outline'}
												onClick={() => conclude(r)}
											>
												{MATRIX_RESULT_LABEL[r]}
											</Button>
										))}
									</div>
								</div>
							) : (
								<div className="flex flex-wrap items-center gap-3">
									<span className="rounded-lg bg-kaefer-dark-grey px-3 py-1.5 text-sm font-bold text-white">
										{
											MATRIX_RESULT_LABEL[
												(prelim as MatrixResult) ?? 'INCONCLUSIVE'
											]
										}
									</span>
									{!m.result && canFill && (
										<Button
											variant="primary"
											onClick={() =>
												conclude((prelim as MatrixResult) ?? 'INCONCLUSIVE')
											}
										>
											Concluir matriz
										</Button>
									)}
								</div>
							)}
							{(m.result === 'INCONCLUSIVE' || prelim === 'INCONCLUSIVE') && (
								<Notice tone="warn">
									Análise inconclusiva: gerar pendências com responsáveis e
									prazos para obter a evidência faltante (registre no plano de
									ação).
								</Notice>
							)}
							{walk.factors.length > 0 && (
								<div>
									<p className="text-xs font-medium uppercase text-muted-foreground">
										Fatores registrados
									</p>
									<ul className="mt-1 list-disc pl-5 text-sm">
										{walk.factors.map((f) => (
											<li key={f}>{f}</li>
										))}
									</ul>
								</div>
							)}
						</Panel>
					) : (
						<Notice>
							A matriz é preenchida por Técnico ou Gestor SSMA. Perfil atual:
							somente leitura.
						</Notice>
					)}

					{m.result && (
						<Panel
							title="Validação (MI-12)"
							description="O comitê e o Gestor SSMA validam a conclusão; a versão fica bloqueada e as causas são liberadas ao plano de ação."
						>
							{validated ? (
								<div className="flex items-start gap-2 text-sm">
									<CheckCircle2 className="mt-0.5 size-5 text-kaefer-grass-green" />
									<div>
										<p>
											Validada por <b>{m.validatedBy}</b> em{' '}
											{m.validatedAt?.replace('T', ' ')}
										</p>
										<p className="text-muted-foreground">
											Parecer: {m.committeeNote}
										</p>
										{isManager && (
											<Button
												size="sm"
												variant="outline"
												className="mt-2 gap-1.5"
												onClick={() => setReopen(true)}
											>
												<RotateCcw className="size-3.5" /> Reabrir matriz
											</Button>
										)}
									</div>
								</div>
							) : isManager ? (
								<div className="space-y-2">
									<Textarea
										rows={3}
										value={note}
										onChange={(e) => setNote(e.target.value)}
										placeholder="Participantes, data e parecer do comitê"
									/>
									<Button
										variant="primary"
										disabled={note.trim().length < 5}
										onClick={validate}
									>
										Validar conclusão
									</Button>
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									Aguardando validação do Gestor SSMA.
								</p>
							)}
						</Panel>
					)}
				</div>

				<Panel
					title="Caminho respondido"
					description={`${walk.path.length} pergunta(s)`}
				>
					<div className="h-1.5 overflow-hidden rounded-full bg-kaefer-light-silver">
						<div
							className="h-full bg-kaefer-honey-yellow"
							style={{ width: `${walk.done ? 100 : progress}%` }}
						/>
					</div>
					<ol className="space-y-2">
						{walk.path.map((q) => {
							const a = m.answers.find((x) => x.questionId === q.id)
							return (
								<li key={q.id} className="rounded-lg border p-2 text-xs">
									<div className="flex items-center justify-between gap-2">
										<span className="font-semibold text-kaefer-dark-grey">
											{q.id}
										</span>
										<span
											className={cn(
												'rounded-full px-2 py-0.5 font-medium',
												a?.answer === 'YES'
													? 'bg-kaefer-grass-green/20'
													: a?.answer === 'NO'
														? 'bg-kaefer-red/15'
														: 'bg-kaefer-honey-yellow/25',
											)}
										>
											{a ? ANSWER_LABEL[a.answer] : '—'}
										</span>
									</div>
									<p className="mt-1 line-clamp-2 text-muted-foreground">
										{q.text}
									</p>
									<p className="mt-1 italic">“{a?.justification}”</p>
									{canFill && (
										<button
											type="button"
											className="mt-1 flex items-center gap-0.5 text-kaefer-sapphire-blue hover:underline"
											onClick={() => {
												setEditing(q.id)
												setJustification(a?.justification ?? '')
											}}
										>
											Alterar resposta <ChevronRight className="size-3" />
										</button>
									)}
								</li>
							)
						})}
						{walk.path.length === 0 && (
							<p className="text-xs text-muted-foreground">
								Nenhuma pergunta respondida.
							</p>
						)}
					</ol>
				</Panel>
			</div>

			<JustificationDialog
				open={reopen}
				onOpenChange={setReopen}
				title="Reabrir matriz investigativa"
				confirmLabel="Reabrir"
				onConfirm={(j) =>
					update(
						occ.id,
						{ matrix: { ...m, validatedBy: null, validatedAt: null } },
						'Matriz reaberta',
						{ detail: j },
					)
				}
			/>
		</div>
	)
}
